import { useCallback, useContext, useSyncExternalStore } from "react";
import { IChat } from "@/lib/types";
import {
    createStreamSession,
    LoadingState,
    SessionCallbacks,
    StartStreamOptions,
    StreamSession,
    StreamSnapshot,
} from "@/lib/streamSession";
import { StreamingContext } from "@/components/context";

/**
 * Holds a Map<chatid, StreamSession> so multiple conversations can stream
 * concurrently. One manager instance lives for the app's lifetime (created once
 * in the provider). Sessions are created lazily and swept after they finish and
 * no component is subscribed to them.
 */
export interface StreamManager {
    startStream: (chat: IChat, options?: StartStreamOptions) => void;
    stopStream: (chatid: string, commitPartial?: boolean) => void;
    getSession: (chatid: string) => StreamSession;
    isStreaming: (chatid: string) => boolean;
    setLoadingState: (chatid: string, updater: LoadingState | ((prev: LoadingState) => LoadingState)) => void;
    clearTokenUsage: (chatid: string) => void;
    destroyAll: () => void;
}

// A finished session with no subscribers is safe to evict; this caps how many
// idle sessions linger before the sweep runs.
const MAX_IDLE_SESSIONS = 20;

export function createStreamManager(getCallbacks: () => SessionCallbacks): StreamManager {
    const sessions = new Map<string, StreamSession>();

    const getSession = (chatid: string): StreamSession => {
        let session = sessions.get(chatid);
        if (!session) {
            session = createStreamSession(chatid, getCallbacks);
            sessions.set(chatid, session);
        }
        return session;
    };

    // Evict finished, unsubscribed sessions so the Map doesn't grow unbounded.
    // Never evict a streaming session or one a component is still subscribed to
    // (its useSyncExternalStore must keep reading the same session object).
    const sweep = () => {
        if (sessions.size <= MAX_IDLE_SESSIONS) return;
        for (const [id, session] of sessions) {
            if (!session.isStreaming() && !session.hasListeners()) {
                session.destroy();
                sessions.delete(id);
            }
        }
    };

    return {
        startStream: (chat, options) => {
            getSession(chat.chatid).start(chat, options);
            sweep();
        },
        stopStream: (chatid, commitPartial) => {
            sessions.get(chatid)?.stop(commitPartial);
        },
        getSession,
        isStreaming: (chatid) => sessions.get(chatid)?.isStreaming() ?? false,
        setLoadingState: (chatid: string, updater) => getSession(chatid).setLoadingState(updater),
        clearTokenUsage: (chatid) => sessions.get(chatid)?.clearTokenUsage(),
        destroyAll: () => {
            for (const session of sessions.values()) session.destroy();
            sessions.clear();
        },
    };
}

// Stable empty snapshot for a null chatid / SSR. Must be a single constant so
// useSyncExternalStore sees a stable reference.
export const EMPTY_SNAPSHOT: StreamSnapshot = {
    streamingBlocks: [],
    committedMessages: [],
    loadingState: { loading: false, thinking: false, thinkingLabel: "" },
    isStreaming: false,
    streamReceiving: false,
    tokenUsage: null,
};

/**
 * Subscribe a component to ONE chat's streaming state. Re-renders only when that
 * chat's session emits — a background chat streaming elsewhere does not re-render
 * the subscriber. Switching chatid resubscribes to the new chat's session.
 */
export function useChatStream(chatid: string | null): StreamSnapshot {
    const manager = useContext(StreamingContext)._manager;

    const subscribe = useCallback(
        (listener: () => void) => {
            if (!chatid) return () => {};
            return manager.getSession(chatid).subscribe(listener);
        },
        [chatid, manager]
    );

    const getSnapshot = useCallback(() => {
        if (!chatid) return EMPTY_SNAPSHOT;
        return manager.getSession(chatid).getSnapshot();
    }, [chatid, manager]);

    return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_SNAPSHOT);
}

/**
 * Lightweight per-chat streaming flag for the navbar: re-renders the subscriber
 * only when the chat's isStreaming flag flips, not on every streamed token.
 */
export function useIsStreaming(chatid: string | null): boolean {
    const manager = useContext(StreamingContext)._manager;

    const subscribe = useCallback(
        (listener: () => void) => {
            if (!chatid) return () => {};
            return manager.getSession(chatid).subscribe(listener);
        },
        [chatid, manager]
    );

    const getSnapshot = useCallback(() => {
        if (!chatid) return false;
        return manager.getSession(chatid).getSnapshot().isStreaming;
    }, [chatid, manager]);

    return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
